const API_BASE = '/api';
const TOKEN_KEY = 'stardust_admin_token';
const LEARNING_TOKEN_KEY = 'stardust_learning_token';
export function setLearningToken(token: string) {
  window.sessionStorage.setItem(LEARNING_TOKEN_KEY, token);
}
export function clearLearningToken() {
  window.sessionStorage.removeItem(LEARNING_TOKEN_KEY);
}
export async function learningRequest<T>(path: string, body?: unknown): Promise<T> {
  const token = window.sessionStorage.getItem(LEARNING_TOKEN_KEY);
  const response = await fetch(`${API_BASE}/public/learning${path}`, {
    signal: AbortSignal.timeout(15000),
    method: body === undefined ? 'GET' : 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) })
  });
  if (response.status === 401) clearLearningToken();
  return parseResponse<T>(response, false);
}
export function fileBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('读取视频分块失败'));
    reader.readAsDataURL(blob);
  });
}

export function getAdminToken() {
  return window.localStorage.getItem(TOKEN_KEY) ?? '';
}

export function setAdminToken(token: string) {
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearAdminToken() {
  window.localStorage.removeItem(TOKEN_KEY);
}

export async function apiGet<T>(path: string): Promise<T> {
  try {
    const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders(), cache: 'no-store' });
    if (response.status === 403 && path !== '/auth/status') {
      window.dispatchEvent(new CustomEvent('stardust-permissions-revoked'));
    }
    return parseResponse<T>(response, path !== '/auth/status');
  } catch (error) {
    if (error instanceof TypeError) throw new Error('后端服务未启动或网络不可用', { cause: error });
    throw error;
  }
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body ?? {})
    });
    return parseResponse<T>(response);
  } catch (error) {
    if (error instanceof TypeError) throw new Error('后端服务未启动或网络不可用', { cause: error });
    throw error;
  }
}

export async function apiDownload(path: string, fallbackName: string) {
  try {
    const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders(), cache: 'no-store' });
    if (response.status === 401) {
      clearAdminToken();
      window.dispatchEvent(new CustomEvent('stardust-auth-expired'));
    }
    if (!response.ok) {
      const text = await response.text();
      let message = `下载失败（${response.status}）`;
      try {
        const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
        message = String(data.message ?? data.error ?? message);
      } catch {
        // Keep the status-based message when the server did not return JSON.
      }
      throw new Error(message);
    }
    return {
      blob: await response.blob(),
      fileName: responseFileName(response.headers.get('Content-Disposition')) || fallbackName
    };
  } catch (error) {
    if (error instanceof TypeError) throw new Error('后端服务未启动或网络不可用', { cause: error });
    throw error;
  }
}

function authHeaders(): Record<string, string> {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function parseResponse<T>(response: Response, admin = true): Promise<T> {
  const text = await response.text();
  let data: Record<string, unknown>;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (error) {
    throw new Error(response.ok ? '接口返回内容无法识别' : `接口请求失败（${response.status}）`, { cause: error });
  }
  if (response.status === 401 && admin) {
    clearAdminToken();
    window.dispatchEvent(new CustomEvent('stardust-auth-expired'));
  }
  if (!response.ok || data?.error) {
    throw Object.assign(new Error(String(data?.message ?? data?.error ?? `请求失败（${response.status}）`)), {
      status: response.status
    });
  }
  return data as T;
}

function responseFileName(contentDisposition: string | null) {
  const encoded = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  return contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] ?? '';
}
